import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, CalendarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import { useMessageRoutes, useDeleteMessageRoute } from '../hooks/useMessageRoutes';
import { useListState } from '../hooks/useListState';
import { useSortableData } from '../hooks/useSortableData';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { ViewToggle } from '../components/ui/ViewToggle';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell, TableHeader } from '../components/ui/DataTable';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import MessageRouteCard from '../components/messageRoutes/MessageRouteCard';
import { useFlag } from '../hooks/useFeatureFlags';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';

export default function MessageRoutesPage() {
  const navigate = useNavigate();
  // The create affordance is gated by its manifest flag, whose
  // member-and-above cohorts carry the role rule (task 692).
  const canCreate = useFlag('innovate.fe.message-routes.create');
  const { page, limit, search, viewMode, setPage, setLimit, setSearch, setViewMode } = useListState({
    entityType: 'message-routes',
  });

  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data, isLoading, error } = useMessageRoutes(page, limit, {
    search: search || undefined,
    active: statusFilter === '' ? undefined : statusFilter === 'active',
  });
  const routes = data?.data || [];
  const pagination = data?.pagination;

  const { sortedData, sortConfig, handleSort } = useSortableData(routes, 'priority', 'asc');

  const deleteMessageRoute = useDeleteMessageRoute();

  const actions = canCreate ? (
    <Link
      to="/message-routes/new"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      New Message Route
    </Link>
  ) : undefined;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading message routes. Please try again.</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Message Routes"
        description={
          pagination
            ? `${pagination.total} ${pagination.total === 1 ? 'route' : 'routes'}${search ? ' found' : ' total'}`
            : 'Routing rules that automate how the platform responds to real-time events'
        }
        actions={actions}
      />

      <div className="mt-6">
        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search routes..."
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="block w-40 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading message routes...</div>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="text-center py-12">
            <ArrowPathIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="text-gray-500 dark:text-gray-400 mb-4 mt-4">
              {search || statusFilter
                ? `No message routes found${search ? ` matching "${search}"` : ''}${statusFilter ? ` with status "${statusFilter}"` : ''}`
                : 'No message routes found. Create your first route to get started.'}
            </div>
            {!search && !statusFilter && canCreate && (
              <Link
                to="/message-routes/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                Create your first route
              </Link>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedData.map((route) => (
              <MessageRouteCard key={route.id} route={route} />
            ))}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <SortableTableHeader
                label="Name"
                field="name"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/4 min-w-0"
              />
              <SortableTableHeader
                label="Channel Pattern"
                field="channelPattern"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/5"
              />
              <TableHeader className="w-1/5">Operations</TableHeader>
              <SortableTableHeader
                label="Endpoint"
                field="endpointPath"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/5"
              />
              <SortableTableHeader
                label="Priority"
                field="priority"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-20"
              />
              <TableHeader className="w-20">Active</TableHeader>
              <SortableTableHeader
                label="Created"
                field="createdAt"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-32"
              />
              <TableHeader className="w-20">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {sortedData.map((route) => (
                <DataTableRow
                  key={route.id}
                  onClick={() => navigate(`/message-routes/${route.id}`)}
                >
                  <DataTableCell>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {route.name}
                      </div>
                      {route.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {route.description}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm font-mono text-gray-600 dark:text-gray-300 truncate">
                      {route.channelPattern}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="flex flex-wrap gap-1">
                      {route.operations.slice(0, 3).map((op) => (
                        <span
                          key={op}
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
                        >
                          {op}
                        </span>
                      ))}
                      {route.operations.length > 3 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          +{route.operations.length - 3}
                        </span>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm font-mono text-gray-600 dark:text-gray-300 truncate">
                      {route.endpointPath}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {route.priority}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        route.active
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {route.active ? 'Active' : 'Inactive'}
                    </span>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(route.createdAt).toLocaleDateString()}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/message-routes/${route.id}`);
                        }}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      >
                        View
                      </button>
                      {/* The one delete affordance (task 864). The inline
                          two-step Confirm/Cancel swap this replaces named
                          nothing that used the resource and offered no way
                          back from a mis-click. */}
                      <DeleteResourceControl
                        flag="innovate.fe.message-routes.delete"
                        entityLabel="message route"
                        name={route.name}
                        id={route.id}
                        stopPropagation
                        body={<p>Messages arriving on it stop being routed.</p>}
                        onDelete={() => deleteMessageRoute.mutateAsync(route.id)}
                      />
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
